import React from 'react';
import { Modal } from './Modal';
import { AlertTriangle } from 'lucide-react';
import clsx from 'clsx';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  loading?: boolean;
  'data-testid'?: string;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'danger',
  loading = false,
  'data-testid': dataTestId,
}) => {
  const variantStyles = {
    danger: {
      icon: 'text-red-500 bg-red-100',
      button: 'bg-red-600 hover:bg-red-700 text-white',
    },
    warning: {
      icon: 'text-yellow-500 bg-yellow-100',
      button: 'bg-yellow-600 hover:bg-yellow-700 text-white',
    },
    info: {
      icon: 'text-blue-500 bg-blue-100',
      button: 'bg-blue-600 hover:bg-blue-700 text-white',
    },
  };

  const styles = variantStyles[variant];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth='sm'
      showCloseButton={!loading}
      dataTestId={dataTestId}
      footer={
        <div className='flex justify-end space-x-3'>
          <button
            data-testid='cancel-delete-button'
            type='button'
            onClick={onClose}
            disabled={loading}
            className='px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {cancelText || 'Cancel'}
          </button>
          <button
            data-testid='confirm-delete-button'
            type='button'
            onClick={onConfirm}
            disabled={loading}
            className={clsx(
              'px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              styles.button
            )}
          >
            {loading ? (
              <span className='flex items-center'>
                <svg
                  className='animate-spin -ml-1 mr-2 h-4 w-4'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
                Processing...
              </span>
            ) : (
              confirmText || (variant === 'danger' ? 'Delete' : 'Confirm')
            )}
          </button>
        </div>
      }
    >
      <div className='flex items-start space-x-4'>
        <div
          className={clsx(
            'flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center',
            styles.icon
          )}
        >
          <AlertTriangle size={24} />
        </div>
        <div className='flex-1'>
          <p className='text-gray-600'>{message}</p>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;
